// Framework + FrameworkVersion CRUD schemas — plan §5 + Ek C Frameworks
// S1 kapsamı: list/get/create/update + versions fork (section/question S2'de).

import { z } from "zod";
import { DerivedSectorPackageSchema } from "./sector-packages.js";

export const FrameworkVersionStatus = z.enum(["draft", "published", "archived"]);
export type FrameworkVersionStatus = z.infer<typeof FrameworkVersionStatus>;

export const VersionNumberSchema = z
  .string()
  .regex(/^v\d{4}\.\d+$/, "Versiyon numarası 'v{yıl}.{N}' formatında olmalı");

export const FrameworkVersionSummarySchema = z.object({
  id: z.string().uuid(),
  versionNumber: VersionNumberSchema,
  status: FrameworkVersionStatus,
  publishedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type FrameworkVersionSummary = z.infer<typeof FrameworkVersionSummarySchema>;

export const FrameworkSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  ownerUserId: z.string().uuid(),
  ownerName: z.string(),
  // M-L2: framework-level L2 prompt — tüm sürümlerde ortak, her zaman editlenebilir.
  frameworkPrompt: z.string(),
  // Raporlama dönemi (K6): çeyreklik proje açma izni + dönem UI'sı rozeti.
  supportsQuarterly: z.boolean(),
  // Bölüm kodları standart referansı mı (GRI/TSRS)? DOCX "İlgili standart" dipnotu yalnız true iken basılır.
  sectionCodesAreStandardRefs: z.boolean(),
  // K6 rozet: bu framework'ten açılmış çeyreklik proje sayısı.
  quarterlyProjectCount: z.number().int(),
  // C6 "kapalıyken görünmez kısıt" rozeti burada DEĞİL: sayı FormManager'da ağaçtan
  // (bölüm ∩ soru kesişimi) türetilir, DTO'ya ayrı bir sayaç konmaz.
  currentVersion: FrameworkVersionSummarySchema.nullable(),
  versionCount: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type FrameworkSummary = z.infer<typeof FrameworkSummarySchema>;

export const FrameworksListResponseSchema = z.object({
  frameworks: z.array(FrameworkSummarySchema),
});
export type FrameworksListResponse = z.infer<typeof FrameworksListResponseSchema>;

export const FrameworkDetailSchema = FrameworkSummarySchema.extend({
  versions: z.array(FrameworkVersionSummarySchema),
});
export type FrameworkDetail = z.infer<typeof FrameworkDetailSchema>;

export const CreateFrameworkRequestSchema = z.object({
  name: z.string().min(2, "Framework adı en az 2 karakter"),
  description: z.string().optional(),
});
export type CreateFrameworkRequest = z.infer<typeof CreateFrameworkRequestSchema>;

export const UpdateFrameworkRequestSchema = z
  .object({
    name: z.string().min(2).optional(),
    description: z.string().nullable().optional(),
    supportsQuarterly: z.boolean().optional(),
    sectionCodesAreStandardRefs: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: "En az bir alan güncellenmeli" });
export type UpdateFrameworkRequest = z.infer<typeof UpdateFrameworkRequestSchema>;

// Version CRUD
export const CreateFrameworkVersionRequestSchema = z.object({
  // Opsiyonel; server otomatik üretir. Manuel override sadece admin tooling için.
  versionNumber: VersionNumberSchema.optional(),
});
export type CreateFrameworkVersionRequest = z.infer<typeof CreateFrameworkVersionRequestSchema>;

// Framework-level L2 prompt güncelle — her zaman editlenebilir (draft/published fark etmez).
export const UpdateFrameworkPromptRequestSchema = z.object({
  frameworkPrompt: z.string().max(20000),
  expectedUpdatedAt: z.string().datetime(),
});
export type UpdateFrameworkPromptRequest = z.infer<typeof UpdateFrameworkPromptRequestSchema>;

export const PublishValidationResultSchema = z.object({
  ok: z.boolean(),
  errors: z.array(z.string()),
  stats: z.object({
    sectionCount: z.number().int(),
    questionCount: z.number().int(),
    requiredQuestionCount: z.number().int(),
  }),
});
export type PublishValidationResult = z.infer<typeof PublishValidationResultSchema>;

// #007 — Publish artık otomatik fork yapmıyor (manuel "Fork" butonu var).
// newDraft alanı kaldırıldı.
// 2026-10-01 — yayınlarken sektör paketi türetme: kullanıcı modalde onay kutularıyla
// hangi `SEK-*` bloklarından paket açılacağını seçer (varsayılan hepsi işaretli).
export const PublishVersionRequestSchema = z
  .object({
    deriveSectorPackages: z.array(z.string().min(1)).max(50).optional(),
  })
  .strict();
export type PublishVersionRequest = z.infer<typeof PublishVersionRequestSchema>;

export const PublishVersionResponseSchema = z.object({
  publishedVersion: FrameworkVersionSummarySchema,
  /** Türetme istenmediyse boş dizi. Türetme hatası yayını ASLA geri almaz. */
  sectorPackages: z.array(DerivedSectorPackageSchema).default([]),
});
export type PublishVersionResponse = z.infer<typeof PublishVersionResponseSchema>;

// Projeler wizard'ı için — yayınlanmış framework+version listesi
export const PublishedFrameworkEntrySchema = z.object({
  frameworkId: z.string().uuid(),
  frameworkName: z.string(),
  version: FrameworkVersionSummarySchema,
  // Raporlama dönemi (K6): wizard'da çeyreklik seçeneğini açar.
  supportsQuarterly: z.boolean(),
  sectionCount: z.number().int(),
  questionCount: z.number().int(),
  // D5 — dönem başına soru sayısı (wizard "bu dönemde N soru" özeti); dört anahtar da her zaman döner.
  periodQuestionCounts: z.object({
    annual: z.number().int(),
    q1: z.number().int(),
    q2: z.number().int(),
    q3: z.number().int(),
  }),
});
export type PublishedFrameworkEntry = z.infer<typeof PublishedFrameworkEntrySchema>;

export const PublishedFrameworksResponseSchema = z.object({
  frameworks: z.array(PublishedFrameworkEntrySchema),
});
export type PublishedFrameworksResponse = z.infer<typeof PublishedFrameworksResponseSchema>;
